import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@roger/ui-components';
import { Loader2, Save } from 'lucide-react';
import EpisodeNotes from '../episode/EpisodeNotes';

export interface VisitTaskNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftNotes: string;
  onDraftNotesChange: (notes: string) => void;
  isDeleted: boolean;
  episodeId?: string | null;
  saveError: string | null;
  isSaving: boolean;
  onSave: () => void;
}

/**
 * Task-notes dialog for a visit chart. On a deleted chart the textarea is
 * read-only and Save is withheld; EpisodeNotes stays reachable so the episode
 * log is not stranded behind a deleted visit.
 */
export const VisitTaskNotesDialog: React.FC<VisitTaskNotesDialogProps> = ({
  open,
  onOpenChange,
  draftNotes,
  onDraftNotesChange,
  isDeleted,
  episodeId,
  saveError,
  isSaving,
  onSave,
}) => (
  <Dialog
    open={open}
    onOpenChange={nextOpen => {
      if (!nextOpen) onOpenChange(false);
    }}
  >
    <DialogContent className='sm:max-w-lg'>
      <DialogHeader>
        <DialogTitle>Task Notes</DialogTitle>
        <DialogDescription>
          {isDeleted
            ? 'This chart is deleted, so its notes are read-only.'
            : 'Notes are visible to everyone who handles this chart.'}
        </DialogDescription>
      </DialogHeader>
      <Textarea
        value={draftNotes}
        onChange={e => onDraftNotesChange(e.target.value)}
        placeholder={
          isDeleted
            ? 'No notes on this chart.'
            : 'Add notes about this chart...'
        }
        className='min-h-[150px]'
        readOnly={isDeleted}
        autoFocus={!isDeleted}
      />
      {saveError && (
        <div className='text-sm text-destructive bg-destructive/10 p-3 rounded-md'>
          {saveError}
        </div>
      )}
      {episodeId && (
        // The episode's shared notes, below the ones specific to this task.
        // Radix unmounts this dialog when it closes, so reopening refetches
        // and a note added elsewhere is never stale here.
        <EpisodeNotes
          episodeId={episodeId}
          canAdd={!isDeleted}
          className='border-t pt-4'
          description='Shared by every task in this episode.'
        />
      )}
      <DialogFooter>
        <Button
          variant='outline'
          onClick={() => onOpenChange(false)}
          disabled={isSaving}
        >
          {isDeleted ? 'Close' : 'Cancel'}
        </Button>
        {!isDeleted && (
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Saving...
              </>
            ) : (
              <>
                <Save className='h-4 w-4' />
                Save Notes
              </>
            )}
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
